/**
 * `/admin/settimana` — la casella «Questa settimana» della Panoramica.
 *
 * Cosa stiamo facendo adesso, in parole nostre. Sostituisce la casella «In
 * corso», che era derivata dagli stati dei moduli e quindi rispondeva «M3 è in
 * beta» a chi chiedeva «cosa state facendo».
 *
 * ## Volutamente povera
 *
 * Niente stato fatto/in-corso, niente scadenze, niente assegnatari: righe e
 * basta. Un mini-planner non lo aggiorna nessuno, e questa pagina è già morta
 * una volta per abbandono — l'abbiamo trovata ferma a maggio dopo tre mesi.
 *
 * L'unica disciplina è la freschezza: la Panoramica mostra quanto è vecchia
 * l'ultima modifica e oltre due settimane spegne il blocco. Qui l'avviso
 * compare in cima, così chi apre il backoffice lo vede prima del cliente.
 */

import { ArrowLeft, AlertTriangle, Eye, EyeOff, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminSettimana,
  useAggiornaVoceSettimana,
  useCreaVoceSettimana,
  useEliminaVoceSettimana,
} from "@/hooks/useAdminSettimana";
import { useMe } from "@/hooks/useMe";
import { useModuli } from "@/hooks/useModuli";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { VoceSettimana } from "@/types/api";

/** Gemello di `GIORNI_STANTIO` in `components/progetto/QuestaSettimana.tsx`. */
const GIORNI_STANTIO = 14;

const INPUT_CLS =
  "w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

function giorniDa(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

export function AdminSettimanaPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { data: voci = [], isLoading } = useAdminSettimana();

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  const ultimoTocco = voci.reduce<string | null>(
    (max, v) => (max === null || v.updated_at > max ? v.updated_at : max),
    null
  );
  const giorni = ultimoTocco ? giorniDa(ultimoTocco) : null;
  const stantio = giorni !== null && giorni > GIORNI_STANTIO;

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Questa settimana
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Cosa stiamo facendo adesso, in parole comprensibili al cliente. Compare
          nella Panoramica accanto alla timeline. Tienila corta: tre o quattro righe
          che dicano il lavoro vero, non l'elenco dei task.
        </p>
      </header>

      {stantio && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning flex items-start gap-2">
          <AlertTriangle className="size-4 shrink-0 mt-0.5" />
          <span>
            Ultima modifica {giorni} giorni fa. Sulla Panoramica il blocco è già
            mostrato spento: aggiornalo o svuotalo.
          </span>
        </div>
      )}

      <NuovaVoce prossimoOrdine={voci.length} />

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : voci.length === 0 ? (
        <p className="rounded-lg border bg-card/40 px-4 py-3 text-sm text-muted-foreground">
          Nessuna voce. La casella nella Panoramica dice «Nessun lavoro in corso
          dichiarato».
        </p>
      ) : (
        <ul className="rounded-lg border bg-card divide-y">
          {voci.map((v) => (
            <VoceRow key={v.id} voce={v} />
          ))}
        </ul>
      )}
    </PageContainer>
  );
}

function VoceRow({ voce: v }: { voce: VoceSettimana }) {
  const aggiorna = useAggiornaVoceSettimana();
  const elimina = useEliminaVoceSettimana();
  const { data: moduli = [] } = useModuli();
  const [conferma, setConferma] = useState(false);
  const [titolo, setTitolo] = useState(v.titolo);
  const [dettaglio, setDettaglio] = useState(v.dettaglio ?? "");

  const isPending = aggiorna.isPending || elimina.isPending;
  const modulo = v.modulo_id ? moduli.find((m) => m.id === v.modulo_id) : undefined;

  function salvaTitolo() {
    const nuovo = titolo.trim();
    if (!nuovo || nuovo === v.titolo) {
      setTitolo(v.titolo);
      return;
    }
    aggiorna.mutate({ id: v.id, body: { titolo: nuovo } });
  }

  function salvaDettaglio() {
    const nuovo = dettaglio.trim();
    if (nuovo === (v.dettaglio ?? "")) return;
    aggiorna.mutate({ id: v.id, body: { dettaglio: nuovo || null } });
  }

  return (
    <li className={cn("px-4 py-3 space-y-2", isPending && "opacity-50")}>
      <div className="flex items-start gap-3">
        <span className="font-mono text-[11px] text-muted-foreground mt-2.5 w-5 shrink-0">
          {v.ordine}
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <input
            type="text"
            value={titolo}
            onChange={(e) => setTitolo(e.target.value)}
            onBlur={salvaTitolo}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            disabled={isPending}
            className={cn(INPUT_CLS, "font-medium")}
            aria-label="Titolo della voce"
          />
          <Textarea
            value={dettaglio}
            onChange={(e) => setDettaglio(e.target.value)}
            onBlur={salvaDettaglio}
            disabled={isPending}
            rows={2}
            placeholder="Dettaglio (facoltativo)"
            aria-label="Dettaglio della voce"
          />
          {modulo && (
            <p className="text-xs text-muted-foreground">
              <span className="font-mono">{modulo.codice}</span> · {modulo.nome}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            disabled={isPending}
            onClick={() =>
              aggiorna.mutate({
                id: v.id,
                body: { is_visibile_cliente: !v.is_visibile_cliente },
              })
            }
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
              "disabled:cursor-not-allowed disabled:opacity-50",
              v.is_visibile_cliente
                ? "bg-success/10 text-success border-success/30 hover:bg-success/20"
                : "bg-muted text-muted-foreground border-muted-foreground/20 hover:bg-muted/70"
            )}
            title={
              v.is_visibile_cliente
                ? "Visibile al cliente — click per nascondere"
                : "Bozza interna — click per mostrarla"
            }
          >
            {v.is_visibile_cliente ? (
              <>
                <Eye className="size-3.5" /> Visibile
              </>
            ) : (
              <>
                <EyeOff className="size-3.5" /> Bozza
              </>
            )}
          </button>

          {conferma ? (
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="destructive"
                disabled={isPending}
                onClick={() => elimina.mutate(v.id)}
              >
                Conferma
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConferma(false)}>
                No
              </Button>
            </div>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              disabled={isPending}
              onClick={() => setConferma(true)}
              aria-label="Elimina voce"
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

function NuovaVoce({ prossimoOrdine }: { prossimoOrdine: number }) {
  const crea = useCreaVoceSettimana();
  const { data: moduli = [] } = useModuli();
  const [titolo, setTitolo] = useState("");
  const [dettaglio, setDettaglio] = useState("");
  const [moduloId, setModuloId] = useState("");

  const valido = titolo.trim() !== "";

  function salva() {
    if (!valido) return;
    crea.mutate(
      {
        titolo: titolo.trim(),
        dettaglio: dettaglio.trim() || null,
        modulo_id: moduloId || null,
        ordine: prossimoOrdine,
      },
      {
        onSuccess: () => {
          setTitolo("");
          setDettaglio("");
          setModuloId("");
        },
      }
    );
  }

  return (
    <div className="rounded-lg border bg-card p-5 space-y-3">
      <h2 className="text-sm font-medium">Aggiungi una riga</h2>
      <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
        <input
          type="text"
          value={titolo}
          onChange={(e) => setTitolo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && valido) salva();
          }}
          placeholder="Stiamo rifacendo la pagina di stato del progetto"
          className={INPUT_CLS}
          // Distinto da «Titolo della voce» delle righe già salvate: due campi
          // che si chiamano quasi uguale non si distinguono né da tastiera né
          // da una prova automatica.
          aria-label="Titolo della nuova riga"
        />
        <select
          value={moduloId}
          onChange={(e) => setModuloId(e.target.value)}
          className={INPUT_CLS}
          aria-label="Modulo collegato"
        >
          <option value="">Nessun modulo</option>
          {moduli.map((m) => (
            <option key={m.id} value={m.id}>
              {m.codice} · {m.nome}
            </option>
          ))}
        </select>
      </div>
      <Textarea
        value={dettaglio}
        onChange={(e) => setDettaglio(e.target.value)}
        rows={2}
        placeholder="Dettaglio (facoltativo) — una frase, non un elenco"
        aria-label="Dettaglio della nuova riga"
      />
      <Button onClick={salva} disabled={!valido || crea.isPending} className="w-fit">
        {crea.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Plus className="size-4" />
        )}
        Aggiungi
      </Button>
    </div>
  );
}
